import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { homeLabsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  TestTube, 
  Package, 
  TrendUp, 
  Clock, 
  CheckCircle,
  SpinnerGap,
  ArrowRight,
  Heart,
  Drop,
  Lightning,
  FirstAidKit
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  ordered: 'bg-blue-100 text-blue-800',
  kit_shipped: 'bg-purple-100 text-purple-800',
  kit_delivered: 'bg-indigo-100 text-indigo-800',
  sample_collected: 'bg-cyan-100 text-cyan-800',
  sample_received: 'bg-teal-100 text-teal-800',
  processing: 'bg-orange-100 text-orange-800',
  resulted: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusLabels = {
  pending: 'Pending',
  ordered: 'Ordered',
  kit_shipped: 'Kit Shipped',
  kit_delivered: 'Kit Delivered',
  sample_collected: 'Sample Collected',
  sample_received: 'Lab Received',
  processing: 'Processing',
  resulted: 'Results Ready',
  cancelled: 'Cancelled'
};

export default function PatientHomeLabsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersRes, resultsRes] = await Promise.all([
        homeLabsAPI.getOrders(),
        homeLabsAPI.getPatientResults(user?.id)
      ]);
      setOrders(ordersRes.data.orders || []);
      setResults(resultsRes.data.results || []);
    } catch (error) {
      console.error('Error loading home labs data:', error);
      toast.error('Failed to load lab data');
    } finally {
      setLoading(false);
    }
  };

  const getProviderLogo = (provider) => {
    if (provider === 'siphox') {
      return <TestTube size={24} className="text-blue-600" />;
    }
    return <Drop size={24} className="text-teal-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-teal-100 rounded-lg">
            <TestTube size={28} weight="duotone" className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              At-Home Lab Tests
            </h1>
            <p className="text-slate-500 text-sm">Track your at-home lab orders and view results</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Package size={24} className="text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">How At-Home Labs Work</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your provider orders a test → Kit ships to your home → You collect a painless sample → 
                Mail it back → Results appear here in 2-10 days
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'orders' ? 'default' : 'outline'}
          onClick={() => setActiveTab('orders')}
          className={activeTab === 'orders' ? 'bg-teal-600' : ''}
        >
          <Package size={18} className="mr-2" />
          My Orders ({orders.length})
        </Button>
        <Button
          variant={activeTab === 'results' ? 'default' : 'outline'}
          onClick={() => setActiveTab('results')}
          className={activeTab === 'results' ? 'bg-teal-600' : ''}
        >
          <CheckCircle size={18} className="mr-2" />
          Results ({results.length})
        </Button>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <TestTube size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No Lab Orders Yet</h3>
                <p className="text-slate-500 mt-1">
                  Your provider can order at-home lab tests for you during your appointment
                </p>
              </CardContent>
            </Card>
          ) : (
            orders.map(order => (
              <Card key={order.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {getProviderLogo(order.provider)}
                      <div>
                        <h3 className="font-semibold text-slate-900">{order.panel?.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">{order.panel?.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            Ordered {new Date(order.created_at).toLocaleDateString()}
                          </span>
                          <span>
                            {order.provider === 'siphox' ? 'SiPhox Health' : 'Rythm Health'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={statusColors[order.status]}>
                        {statusLabels[order.status]}
                      </Badge>
                      {order.status === 'resulted' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-2 text-teal-600"
                          onClick={() => {
                            setActiveTab('results');
                          }}
                        >
                          View Results <ArrowRight size={14} className="ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Status Timeline */}
                  {order.status !== 'pending' && order.status !== 'resulted' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`w-2 h-2 rounded-full ${order.status === 'kit_shipped' ? 'bg-purple-500' : 'bg-green-500'}`} />
                        <span className="text-slate-600">
                          {order.status === 'kit_shipped' && 'Your kit is on the way!'}
                          {order.status === 'kit_delivered' && 'Kit delivered - please collect your sample'}
                          {order.status === 'sample_collected' && 'Sample collected - mail it back'}
                          {order.status === 'sample_received' && 'Lab received your sample'}
                          {order.status === 'processing' && 'Processing your results...'}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <div className="space-y-4">
          {results.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <TrendUp size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No Results Yet</h3>
                <p className="text-slate-500 mt-1">
                  Results will appear here once your lab tests are processed
                </p>
              </CardContent>
            </Card>
          ) : (
            results.map(result => (
              <Card key={result.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{result.panel_name}</CardTitle>
                      <CardDescription>
                        {new Date(result.result_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </CardDescription>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      {result.provider === 'siphox' ? 'SiPhox' : 'Rythm'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {result.results && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(result.results).slice(0, 8).map(([key, value]) => (
                        <div key={key} className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500 truncate">{key}</p>
                          <p className="font-semibold text-slate-900">
                            {typeof value === 'object' ? value.value : value}
                            {typeof value === 'object' && value.unit && (
                              <span className="text-xs text-slate-500 ml-1">{value.unit}</span>
                            )}
                          </p>
                          {typeof value === 'object' && value.status && (
                            <Badge 
                              className={`text-xs mt-1 ${
                                value.status === 'normal' ? 'bg-green-100 text-green-700' :
                                value.status === 'high' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {value.status}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {result.results && Object.keys(result.results).length > 8 && (
                    <p className="text-sm text-slate-500 mt-3">
                      + {Object.keys(result.results).length - 8} more biomarkers
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
